import React, { useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  DoughnutController,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(DoughnutController, ArcElement, Tooltip, Legend);

function OverviewCharts({ applicationsStats = {} }) {
  const chartRefs = useRef({}); // Store chart instances to clean up later

  const createCircularChart = (canvasId, percentage, count, backgroundColor, borderColor, label, textColor) => {
    const ctx = document.getElementById(canvasId).getContext("2d");

    // Destroy the previous chart instance if it exists
    if (chartRefs.current[canvasId]) {
      chartRefs.current[canvasId].destroy();
    }

    // Create a new chart instance
    chartRefs.current[canvasId] = new ChartJS(ctx, {
      type: "doughnut",
      data: {
        labels: [label, ""], // Add labels for tooltips
        datasets: [
          {
            data: [percentage, 100 - percentage],
            backgroundColor: [backgroundColor, "#f3f4f6"], // Inner color
            borderColor: [borderColor, "#f3f4f6"], // Outline color
            borderWidth: 1, // Outline width (1px)
          },
        ],
      },
      options: {
        cutout: "70%",
        plugins: {
          tooltip: {
            enabled: true, // Enable tooltips
            bodyFont: {
              size: 10, // Increase font size
            },
            padding: 12, // Increase padding
            callbacks: {
              label: (context) => {
                const label = context.label || "";
                const value = context.raw || 0;
                return `${label}: ${value.toFixed(1)}% (${count})`; // Format percentage to 1 decimal place
              },
            },
          },
          legend: { display: false },
        },
        responsive: true,
      },
      plugins: [
        {
          id: "text",
          beforeDraw(chart) {
            const {
              width,
              height,
              ctx,
              data: { datasets },
            } = chart;
            ctx.save();
            ctx.font = "bold 16px Arial";
            ctx.fillStyle = textColor; // Use the text color for the percentage text
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            const text = `${datasets[0].data[0].toFixed(1)}%`; // Format percentage to 1 decimal place
            const centerX = width / 2;
            const centerY = height / 2;
            ctx.fillText(text, centerX, centerY);
          },
        },
      ],
    });
  };

  useEffect(() => {
    const { percentages = {}, byStatus = {} } = applicationsStats;

    // Initialize charts with dynamic data and updated colors
    createCircularChart(
      "chartPending",
      percentages.Pending || 0,
      byStatus.Pending || 0, // Count for Pending
      "rgba(54, 162, 235, 0.2)", // Background color
      "rgba(54, 162, 235, 1)", // Border color
      "Pending",
      "#3b82f6" // Text color (blue-500)
    );
    createCircularChart(
      "chartInProcess",
      percentages["In Process"] || 0,
      byStatus["In Process"] || 0, // Count for In Process
      "rgba(255, 206, 86, 0.2)", // Background color
      "rgba(255, 206, 86, 1)", // Border color
      "In Process",
      "#eab308" // Text color (yellow-500)
    );
    createCircularChart(
      "chartAccepted",
      percentages.Accepted || 0,
      byStatus.Accepted || 0, // Count for Accepted
      "rgba(75, 192, 192, 0.2)", // Background color
      "rgba(75, 192, 192, 1)", // Border color
      "Accepted",
      "#22c55e" // Text color (green-500)
    );
    createCircularChart(
      "chartRejected",
      percentages.Rejected || 0,
      byStatus.Rejected || 0, // Count for Rejected
      "rgba(255, 99, 132, 0.2)", // Background color
      "rgba(255, 99, 132, 1)", // Border color
      "Rejected",
      "#ef4444" // Text color (red-500)
    );

    // Cleanup on component unmount
    return () => {
      Object.values(chartRefs.current).forEach((chart) => chart.destroy());
    };
  }, [applicationsStats]); // Re-run effect when `applicationsStats` changes

  return (
    <div className="flex flex-wrap space-x-6">
      {/* Right Card */}
      <div className="flex flex-col space-y-6 w-full">
        {/* Graph Card */}
        <div className="px-10 py-6 bg-white rounded-lg shadow-lg">
          {/* Heading */}
          {/* <h2 className="text-lg font-medium text-gray-500 mb-4">Applications Overview</h2> */}

          {/* Total Count */}
          <div className="mb-6">
            <p className="text-lg font-semibold text-gray-700">
              Total Applications: <span className="text-blue-600">{applicationsStats.total || 0}</span>
            </p>
          </div>

          {/* Circular Progress Indicators */}
          <div className="flex justify-around items-center">
            {/* Pending Applications */}
            <div className="text-center">
              <canvas id="chartPending" className="size-32"></canvas>
              <p className="text-blue-500 mt-2">Pending</p>
            </div>
            {/* In Process Applications */}
            <div className="text-center">
              <canvas id="chartInProcess" className="size-32"></canvas>
              <p className="text-yellow-500 mt-2">In Process</p>
            </div>
            {/* Accepted Applications */}
            <div className="text-center">
              <canvas id="chartAccepted" className="size-32"></canvas>
              <p className="text-green-500 mt-2">Accepted</p>
            </div>
            {/* Rejected Applications */}
            <div className="text-center">
              <canvas id="chartRejected" className="size-32"></canvas>
              <p className="text-red-500 mt-2">Rejected</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OverviewCharts;